import { useCallback, useEffect, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { useApi } from "../views/hooks/useApi"
import {
  ChatRequest,
  FinalEvent,
  ResponseSection,
  WarrenStreamEvent,
} from "../views/pages/Warren/types"

/**
 * Accumulated state for Warren chat streaming
 */
export interface WarrenChatState {
  events: WarrenStreamEvent[]
  progressMessages: string[]
  isStreaming: boolean
  isComplete: boolean
  error: string | null
  sections?: ResponseSection[]
  conversation?: Record<string, any> | null
  context?: Record<string, any> | null
}

const initialState: WarrenChatState = {
  events: [],
  progressMessages: [],
  isStreaming: false,
  isComplete: false,
  error: null,
}

/**
 * Hook to execute a Warren chat query with streaming using async generators
 */
export const useWarrenChat = () => {
  const { scientApi } = useApi()
  const [state, setState] = useState<WarrenChatState>(initialState)
  const abortControllerRef = useRef<AbortController | null>(null)

  const sendQuery = useCallback(
    async (request: ChatRequest) => {
      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController()

      // Reset state and start streaming
      setState({
        ...initialState,
        isStreaming: true,
      })

      try {
        // Get the async generator
        const stream = scientApi.warren.streamQuery(request, abortControllerRef.current.signal)

        // Consume the stream
        for await (const event of stream) {
          // Use flushSync to force immediate render after each event
          // This prevents React 18 from batching all updates together
          flushSync(() => {
            setState(prevState => {
              if (event.type === "final") {
                const completeEvent = event as FinalEvent

                return {
                  ...prevState,
                  events: [...prevState.events, event],
                  isStreaming: false,
                  isComplete: true,
                  sections: completeEvent.response.sections,
                  conversation: completeEvent.messages,
                  context: completeEvent.context,
                }
              } else {
                // Progress event

                return {
                  ...prevState,
                  events: [...prevState.events, event],
                  progressMessages: [...prevState.progressMessages, event.message],
                }
              }
            })
          })
        }
      } catch (error: any) {
        // Handle abort and other errors
        if (error.name !== "AbortError") {
          setState(prevState => ({
            ...prevState,
            isStreaming: false,
            isComplete: true,
            error: error.message || "An error occurred",
          }))
        }
      }
    },
    [scientApi],
  )

  const reset = useCallback(() => {
    setState(initialState)
  }, [])

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setState(prevState => ({
      ...prevState,
      isStreaming: false,
    }))
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    ...state,
    sendQuery,
    reset,
    cancel,
  }
}
